import {
  DeleteTwoTone,
  EditTwoTone,
  LoadingOutlined,
  PlusOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import {
  Button,
  Popover,
  Select,
  Table,
  message,
  Row,
  Col,
  Spin,
  Input,
  Tag,
} from "antd";
import Link from "antd/es/typography/Link";
import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../Common/MainLayout";
import PracticeSubmissionHoldsService, {
  ApplicationPracticeSubmissionType,
  ApplicationPracticeSubmissionTypeList,
  SubmissionHoldType,
  SubmissionHoldTypeList,
} from "../../Services/PracticeSubmissionHoldsService";
import { useParams } from "react-router-dom";
import SubmissionHoldForm from "./SubmissionHoldForm";
import {
  DBRecordStatus,
  IPagingPagingItems,
  SubmissionHoldStatusOptions,
} from "../../Common/CommonComponent";
import { adminContext } from "../../Common/PageRoute";
import { IIdName } from "../../Services/CommonService";
import { ColumnsType } from "antd/es/table";
import { Dayjs } from "dayjs";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import { Utils } from "../../Utilities/Utils";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";
import { usePractice } from "../../Context/PracticeContext";

export interface submissionHoldListDataType {
  key: React.Key;
  createdBy: any;
  filingTypes: ApplicationPracticeSubmissionType[];
  from: string;
  id: string;
  practices: IIdName[];
  recordStatus: DBRecordStatus;
  to: string;
  type: SubmissionHoldType;
  updatedBy: any;
  warningMessage: string;
}

const { Search } = Input;
const SubmissionHoldList = () => {
  const { openNotification } = React.useContext(adminContext);
  const { allPracticeIds } = usePractice();
  const [submissionHoldList, setSubmissionHoldList] = useState<
    submissionHoldListDataType[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = React.useState<{ type: string; params: any }>({
    type: "",
    params: {},
  });
  const [pagingItems, setPagingItems] = useState<IPagingPagingItems>({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 15,
  });
  const [refresh, setRefresh] = useState(0);
  const { id }: any = useParams();
  const practiceId: number = Number(id);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRecord, setModalRecord] =
    useState<submissionHoldListDataType | null>(null);

  const [filterParams, setFilterParams] = React.useState<{
    practiceIds: number[];
    status: DBRecordStatus;
    search: string;
    sortDir: string;
    sortCol: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    errorType: SubmissionHoldType | null;
    filingTypes: ApplicationPracticeSubmissionType[];
  }>({
    practiceIds: id ? [Number(id)] : [],
    status: DBRecordStatus.Active,
    search: "",
    sortDir: "descend",
    sortCol: "from",
    fromDate: null,
    toDate: null,
    errorType: null,
    filingTypes: [],
  });

  const TypeNames = useMemo(
    () => ({ 0: "Error", 1: "Warning" }) as Record<number, string>,
    [],
  );

  const getSubmissionHold = async (page: number, pageSize: number) => {
    try {
      setLoading(true);

      const start = (page - 1) * pageSize;

      const res = await PracticeSubmissionHoldsService.get(
        filterParams.search,
        start,
        pageSize,
        filterParams.sortCol,
        filterParams.sortDir,
        filterParams.status,
        filterParams.fromDate?.startOf("day").toISOString(),
        filterParams.toDate?.endOf("day").toISOString(),
        filterParams.errorType ?? undefined,
        filterParams.practiceIds,
        filterParams.filingTypes,
      );
      if (res.status) {
        setSubmissionHoldList(res.result?.items || []);
        setPagingItems({
          totalRecords: res.result?.totalRecords,
          currentPage: page,
          pageSize: pageSize,
        });
      }
    } catch (err) {
      message.error("Failed to load submission holds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubmissionHold(pagingItems.currentPage, pagingItems.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, filterParams]);

  const onClickToggleUserStatus = (record: any, newStatus: DBRecordStatus) => {
    setDialog({
      type: "isActive",
      params: {
        record: record,
        newStatus: newStatus,
        name: record.warningMessage,
        id: record.id,
        status: record.recordStatus,
      },
    });
  };

  const columns: ColumnsType<submissionHoldListDataType> = [
    { title: "Message", dataIndex: "warningMessage", key: "m", sorter: true },
    {
      title: "From",
      dataIndex: "from",
      key: "from",
      sorter: true,
      render: (record: any) =>
        record
          ? new Date(record).toLocaleString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      title: "To",
      dataIndex: "to",
      key: "to",
      sorter: true,
      render: (record: any) =>
        record
          ? new Date(record).toLocaleString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      title: "Practices",
      dataIndex: "practices",
      key: "practices",
      render: (practices: IIdName[] = []) => {
        if (!practices?.length) return "-";
        const practiceName = practices[0]?.name;
        const remaining = practices.length - 1;
        return (
          <>
            {practices?.length > 0 && (
              <Row gutter={8}>
                <Col>{practiceName}</Col>
                <Col>
                  <Popover
                    placement="bottom"
                    content={
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={practices.map((p, i) => ({
                          key: i,
                          sno: i + 1,
                          name: p.name,
                        }))}
                        columns={[
                          {
                            title: "S.No.",
                            dataIndex: "sno",
                            width: 50,
                          },
                          {
                            title: "Practice Name",
                            dataIndex: "name",
                          },
                        ]}
                      />
                    }
                  >
                    <div
                      style={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {/* +N */}
                      {remaining > 0 && (
                        <Tag color="blue">+{remaining}</Tag>
                      )}
                    </div>
                  </Popover>
                </Col>
              </Row>
            )}
          </>
        );
      },
    },
    {
      title: "Filing Types",
      dataIndex: "filingTypes",
      key: "filingTypes",
      render: (filingTypes: number[] = []) => {
        if (!filingTypes?.length) return "-";
        const names = filingTypes
          .map((ft) => Utils.getFilingTypeName(ft) || ft)
          .filter((name) => !!name);

        const display = names.slice(0, 3).join(", ");
        const extra = names.length - 3;
        return (
          <>
            {filingTypes?.length > 0 && (
              <Row gutter={8}>
                <Col>
                  <span>{display}</span>
                </Col>
                <Col>
                  <Popover
                    placement="bottom"
                    content={
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={filingTypes.map((ft, i) => ({
                          key: i,
                          sno: i + 1,
                          name: Utils.getFilingTypeName(ft) || ft,
                        }))}
                        columns={[
                          {
                            title: "S.No.",
                            dataIndex: "sno",
                            width: 60,
                          },
                          {
                            title: "Filing Type",
                            dataIndex: "name",
                          },
                        ]}
                      />
                    }
                  >
                    <div
                      style={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {/* +N */}
                      {extra > 0 && <Tag color="blue">+{extra}</Tag>}
                    </div>
                  </Popover>
                </Col>
              </Row>
            )}
          </>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (t: number) => TypeNames[t] || "-",
    },
    {
      title: "Action",
      width: "10%",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: 24, textAlign: "center" }}>
            <Link
              className="edit-btn"
              onClick={() => {
                setModalRecord(record);
                setModalOpen(true);
              }}
            >
              <EditTwoTone />
            </Link>
          </div>
          <div style={{ width: 24, textAlign: "center" }}>
            <Link
              className="status-btn"
              onClick={async () => {
                onClickToggleUserStatus(
                  record,
                  record.recordStatus === DBRecordStatus.Deleted
                    ? DBRecordStatus.Active
                    : DBRecordStatus.Deleted,
                );
                // try {
                //   // const newStatus = record.recordStatus === 1 ? 0 : 1;
                //   // await PracticeSubmissionHoldsService.update(record.id, { ...record, recordStatus: newStatus });
                //   // message.success(newStatus === 1 ? "Record deactivated" : "Record activated");

                //   // setRefresh((x) => x + 1);
                // } catch (err) {
                //   message.error("Failed to update status");
                // }
              }}
            >
              {record.recordStatus === DBRecordStatus.Deleted ? (
                <RollbackOutlined twoToneColor="#52c41a" />
              ) : (
                <DeleteTwoTone twoToneColor="#ff4d4f" />
              )}
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const onTablePageChange = (
    paging: { current?: number; pageSize?: number },
    filter: Record<string, any>,
    sort: { order?: string; columnKey?: string },
  ) => {
    setFilterParams((prev: any) => {
      return {
        ...prev,
        sortDir: sort?.order,
        sortCol: sort?.columnKey,
      };
    });
    if (paging?.current && paging?.pageSize) {
      setPagingItems((prev: any) => {
        return {
          ...prev,
          currentPage: paging?.current,
          pageSize: paging?.pageSize,
        };
      });
    }
  };

  const setListParamsAndRefresh = (key: any, value: any) => {
    setPagingItems({ ...pagingItems, currentPage: 1, pageSize: 15 });
    setFilterParams((p: any) => ({ ...p, [key]: value }));
  };

  return (
    <>
      <Row style={{ height: 100 }}>
        {!practiceId && (
          <Row justify="start" align="middle" style={{ margin: "1px 15px" }}>
            <PageHeader
              item={[{ label: "Submission hold" }]}
              style={{ color: "#172B4D" }}
            />
          </Row>
        )}
        <Row className={id ? "" : "p-3"} style={{ backgroundColor: "white" }}>
          <Col span={24}>
            <Row gutter={[8, 8]} className="pb-3" align="middle">
              {/* ================= ADD ================= */}
              <Col xs={12} sm={12} md={4} lg={3} xl={2} xxl={2}>
                <Button
                  type="primary"
                  className="add-btn"
                  block
                  onClick={() => {
                    setModalRecord(null);
                    setModalOpen(true);
                  }}
                >
                  <PlusOutlined /> Add
                </Button>
              </Col>

              {/* ================= SEARCH ================= */}
              <Col
                xs={12}
                sm={12}
                md={5}
                lg={{ span: 4, offset: 0 }}
                xl={{ span: 3, offset: id ? 4 : 0 }}
                xxl={{ span: 3, offset: id ? 7 : 3 }}
              >
                <Search
                  placeholder="Search"
                  onSearch={(val) => setListParamsAndRefresh("search", val)}
                  onChange={(e) =>
                    e.target.value === ""
                      ? setListParamsAndRefresh("search", "")
                      : null
                  }
                  style={{ width: "100%" }}
                />
              </Col>

              {/* ================= DATE ================= */}
              <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
                <CustomRangePicker
                  value={[filterParams.fromDate, filterParams.toDate]}
                  onChange={(dates) => {
                    setFilterParams((prev) => ({
                      ...prev,
                      fromDate: dates?.[0] || null,
                      toDate: dates?.[1] || null,
                    }));
                    setPagingItems({ ...pagingItems, currentPage: 1 });
                  }}
                />
              </Col>

              {/* ================= PRACTICES ================= */}
              {!practiceId && (
                <Col xs={24} sm={12} md={7} lg={6} xl={4} xxl={4}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select Practices"
                    maxTagCount="responsive"
                    style={{ width: "100%" }}
                    value={filterParams.practiceIds}
                    onChange={(val) =>
                      setListParamsAndRefresh("practiceIds", val)
                    }
                    options={allPracticeIds}
                    filterOption={(input, option) =>
                      (option?.label?.toString() ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Col>
              )}

              {/* ================= SUBMISSION HOLD TYPE ================= */}
              <Col xs={24} sm={12} md={9} lg={5} xl={4} xxl={4}>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select Submission Type"
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                  value={filterParams.filingTypes}
                  onChange={(val) =>
                    setListParamsAndRefresh("filingTypes", val)
                  }
                  options={ApplicationPracticeSubmissionTypeList?.map((x) => ({
                    value: x.value,
                    label: x.label,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Col>

              {/* ================= TYPE ================= */}
              <Col xs={12} sm={12} md={8} lg={3} xl={3} xxl={2}>
                <Select
                  placeholder="All Type"
                  style={{ width: "100%" }}
                  value={filterParams.errorType}
                  onChange={(val) => setListParamsAndRefresh("errorType", val)}
                  options={SubmissionHoldTypeList?.map((x: any) => ({
                    value: x.value,
                    label: x.label,
                  }))}
                />
              </Col>

              {/* ================= STATUS ================= */}
              <Col xs={12} sm={12} md={7} lg={4} xl={3} xxl={2}>
                <Select
                  placeholder="Status"
                  style={{ width: "100%" }}
                  value={filterParams.status}
                  onChange={(val) => setListParamsAndRefresh("status", val)}
                  options={SubmissionHoldStatusOptions?.map((x: any) => ({
                    value: x.value,
                    label: x.label,
                  }))}
                />
              </Col>
            </Row>
          </Col>

          <Col
            span={24}
          // style={{ minHeight: window.outerHeight - 200 }}
          >
            <Table
              dataSource={submissionHoldList}
              columns={columns}
              size="small"
              scroll={{ x: 1200 }}
              // scroll={{ x: 1200, y: window.innerHeight - 240 }}
              className="border-top-table"
              virtual
              // loading={loading}
              locale={{
                triggerDesc: "",
                triggerAsc: "",
                cancelSort: "",
              }}
              loading={{
                spinning: loading,
                indicator: <Spin indicator={<LoadingOutlined />} />,
              }}
              pagination={{
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                total: pagingItems.totalRecords,
                current: pagingItems.currentPage,
                pageSize: pagingItems.pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["15", "25", "50", "100"],
              }}
              onChange={(paging, filter, sort: any) =>
                onTablePageChange(paging, filter, sort)
              }
            // rowKey="id"
            />
          </Col>
        </Row>
      </Row>
      {modalOpen && (
        <SubmissionHoldForm
          modelOpen={modalOpen}
          record={modalRecord}
          onDismiss={(ok: boolean) => {
            setModalOpen(false);
            if (ok) setRefresh((x) => x + 1);
          }}
          practiceOptions={allPracticeIds}
        />
      )}
      {dialog.type === "isActive" && (
        <ConfirmationModal
          open={true}
          onNo={() => setDialog({ type: "", params: "" })}
          onYes={async () => {
            try {
              // const apiData = await UserService.toggleUserStatus(
              //   dialog.params?.id,
              //   dialog.params?.status === DBRecordStatus.Deleted
              //     ? DBRecordStatus.Active
              //     : DBRecordStatus.Deleted
              // );

              const apiData = await PracticeSubmissionHoldsService.update(
                dialog.params?.id,
                {
                  ...dialog.params.record,
                  recordStatus: dialog.params.newStatus,
                },
              );
              if (apiData.status) {
                openNotification(
                  "success",
                  `Submission hold "${dialog?.params?.name}" ${dialog?.params?.status === DBRecordStatus.Deleted
                    ? "Active"
                    : "Delete"
                  } sucessfully.`,
                );

                setRefresh((x) => x + 1);
                setDialog({ type: "", params: "" });
              } else {
                openNotification("error", apiData?.message);
                console.log("error", apiData?.message);
                setDialog({ type: "", params: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setDialog({ type: "", params: "" });
            }
          }}
          text={`Are you sure you want to ${dialog?.params?.status === DBRecordStatus.Deleted
            ? "Active"
            : "Delete"
            }  this "${dialog?.params?.name}" Submission hold. ?`}
        />
      )}
    </>
  );
};

export default SubmissionHoldList;
